import { Box, Card, Container, FormControlLabel, Paper, Typography } from '@mui/material';
import styled from 'styled-components';
import { color, duration, ease, font, radius, shadow } from '../../../theme/tokens';
import { reducedMotion, shake } from '../../atome/motion/motion.styled';

export const CheckoutWrapper = styled(Box)`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  /* The order summary enters from the right (Reveal from="right"), so it sits
     40px past the edge until it plays — enough to scroll the page sideways on
     a phone. Every other section hosting a sideways reveal clips it; this one
     had nothing. Note: clip, not hidden, so the summary card stays sticky. */
  overflow-x: clip;
  background:
    radial-gradient(70% 50% at 90% 0%, ${color.sandLight} 0%, transparent 55%),
    ${color.cream};
`;

export const CheckoutContainer = styled(Container)`
  flex: 1;
  padding-top: 2.5rem !important;
  padding-bottom: 3.5rem !important;
`;

export const CheckoutHead = styled.div`
  text-align: center;
  margin-bottom: 2.4rem;
`;

export const CheckoutTitle = styled(Typography)`
  && {
    font-family: ${font.display};
    font-weight: 600;
    font-size: clamp(2.2rem, 5vw, 3.2rem);
    line-height: 1.08;
    color: ${color.ink};
    margin: 0.7rem 0 0.4rem;
  }
`;

export const CheckoutSubtitle = styled(Typography)`
  && {
    font-family: ${font.body};
    color: ${color.inkSoft};
    font-size: 0.98rem;
  }
`;

/** Contact -> Delivery -> Payment progress rail. */
export const StepRail = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  margin-top: 1.6rem;
  flex-wrap: wrap;
`;

export const Step = styled.div<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  font-family: ${font.body};
  font-size: 0.78rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${({ $active }) => ($active ? color.forest : color.inkFaint)};
  transition: color ${duration.base} ${ease.out};

  b {
    width: 24px;
    height: 24px;
    display: grid;
    place-items: center;
    border-radius: 50%;
    font-size: 0.72rem;
    color: ${({ $active }) => ($active ? '#fff' : color.inkFaint)};
    background: ${({ $active }) => ($active ? color.forest : 'rgba(74, 112, 97, 0.12)')};
    transition: background ${duration.base} ${ease.out}, color ${duration.base} ${ease.out};
  }
`;

export const StepLine = styled.span`
  width: 34px;
  height: 1px;
  background: ${color.line};

  @media (max-width: 600px) {
    display: none;
  }
`;

export const StyledPaper = styled(Paper)`
  && {
    position: relative;
    padding: 2rem 1.9rem;
    margin-bottom: 1.4rem;
    border-radius: ${radius.lg};
    background: rgba(255, 255, 255, 0.9);
    backdrop-filter: blur(8px);
    border: 1px solid rgba(255, 255, 255, 0.8);
    box-shadow: ${shadow.soft};
    transition: box-shadow ${duration.base} ${ease.out}, transform ${duration.base} ${ease.out};
  }

  &&:focus-within {
    box-shadow: 0 18px 44px rgba(58, 48, 38, 0.12);
    transform: translateY(-3px);
  }

  @media (max-width: 600px) {
    && {
      padding: 1.5rem 1.15rem;
    }
  }

  ${reducedMotion};
`;

export const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.85rem;
  margin-bottom: 1.4rem;
`;

export const SectionNumber = styled.span`
  width: 34px;
  height: 34px;
  flex: 0 0 auto;
  display: grid;
  place-items: center;
  border-radius: 50%;
  background: ${color.sand};
  color: ${color.forestDeep};
  font-family: ${font.body};
  font-size: 0.85rem;
  font-weight: 600;
`;

export const SectionTitle = styled(Typography)`
  && {
    font-family: ${font.display};
    font-weight: 600;
    font-size: 1.5rem;
    line-height: 1.2;
    color: ${color.ink};
    margin: 0;
  }

  && + span {
    font-family: ${font.body};
    font-size: 0.82rem;
    color: ${color.inkFaint};
  }
`;

export const PaymentMethodLabel = styled(FormControlLabel)<{ $disabled?: boolean }>`
  && {
    margin: 0 0 0.7rem;
    padding: 0.9rem 1.1rem;
    width: 100%;
    border-radius: ${radius.md};
    border: 1px solid ${color.line};
    background: ${({ $disabled }) => ($disabled ? 'rgba(226, 213, 196, 0.16)' : 'transparent')};
    opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};
    transition:
      border-color ${duration.base} ${ease.out},
      background ${duration.base} ${ease.out},
      transform ${duration.fast} ${ease.out};
  }

  &&:hover {
    border-color: ${({ $disabled }) => ($disabled ? color.line : color.forest)};
    transform: ${({ $disabled }) => ($disabled ? 'none' : 'translateX(3px)')};
  }

  && .MuiFormControlLabel-label {
    font-family: ${font.body};
    font-weight: 500;
    color: ${color.ink};
    width: 100%;
  }

  ${reducedMotion};
`;

export const MethodHint = styled.span`
  display: block;
  font-size: 0.8rem;
  font-weight: 400;
  color: ${color.inkSoft};
  margin-top: 0.15rem;
`;

export const SoonTag = styled.span`
  margin-left: 0.6rem;
  padding: 0.15rem 0.55rem;
  border-radius: ${radius.pill};
  background: ${color.sand};
  color: ${color.forestDeep};
  font-size: 0.66rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
`;

/* ----------------------------- summary ----------------------------- */

export const OrderSummaryCard = styled(Card)`
  && {
    position: sticky;
    top: 96px;
    border-radius: ${radius.lg};
    background: rgba(255, 255, 255, 0.92);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.8);
    box-shadow: ${shadow.soft};
    overflow: hidden;
  }

  &&::before {
    content: '';
    display: block;
    height: 4px;
    background: linear-gradient(90deg, ${color.forest}, ${color.sand});
  }
`;

export const ProductRow = styled(Box)`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.1rem;
  padding-bottom: 1.1rem;
  border-bottom: 1px solid ${color.lineSoft};
`;

export const ProductImage = styled.img`
  width: 76px;
  height: 76px;
  flex: 0 0 auto;
  object-fit: cover;
  border-radius: ${radius.md};
  background: ${color.creamDeep};
  transition: transform ${duration.base} ${ease.out};

  &:hover {
    transform: scale(1.06) rotate(-1.5deg);
  }

  ${reducedMotion};
`;

export const SummaryQty = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  margin-top: 0.4rem;

  button {
    width: 26px;
    height: 26px;
    display: grid;
    place-items: center;
    border-radius: 50%;
    cursor: pointer;
    color: ${color.forest};
    background: transparent;
    border: 1px solid ${color.sandDeep};
    transition: background ${duration.fast} ${ease.out}, color ${duration.fast} ${ease.out};
  }

  button:hover:not(:disabled) {
    background: ${color.forest};
    border-color: ${color.forest};
    color: #fff;
  }

  button:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  span {
    min-width: 1.8rem;
    text-align: center;
    font-family: ${font.body};
    font-size: 0.92rem;
    color: ${color.ink};
  }
`;

export const SummaryRow = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.7rem;
  font-family: ${font.body};
`;

export const TotalRow = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid ${color.lineSoft};

  span:last-child {
    font-family: ${font.display};
    font-size: 1.85rem;
    font-weight: 600;
    color: ${color.forest};
  }

  span:first-child {
    font-family: ${font.body};
    font-size: 0.95rem;
    color: ${color.ink};
  }
`;

export const SummaryNote = styled.p`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  margin: 1.2rem 0 0;
  font-family: ${font.body};
  font-size: 0.78rem;
  line-height: 1.6;
  color: ${color.inkSoft};

  svg {
    color: ${color.forest};
    font-size: 1rem;
    margin-top: 1px;
  }
`;

export const SubmitButton = styled.button`
  position: relative;
  overflow: hidden;
  width: 100%;
  padding: 1.05rem 1.5rem;
  margin-top: 0.4rem;
  border: none;
  border-radius: ${radius.pill};
  cursor: pointer;
  font-family: ${font.body};
  font-size: 1rem;
  font-weight: 500;
  letter-spacing: 0.05em;
  color: #fff;
  background: ${color.forest};
  box-shadow: 0 16px 34px rgba(74, 112, 97, 0.26);
  transition:
    transform ${duration.base} ${ease.spring},
    box-shadow ${duration.base} ${ease.out},
    background ${duration.base} ${ease.out};

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -120%;
    width: 55%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.38), transparent);
    transition: left 0.75s ${ease.out};
  }

  &:hover:not(:disabled) {
    background: ${color.forestDeep};
    transform: translateY(-3px);
    box-shadow: 0 22px 44px rgba(74, 112, 97, 0.32);
  }

  &:hover:not(:disabled)::before {
    left: 130%;
  }

  &:disabled {
    background: ${color.sandDeep};
    cursor: not-allowed;
    box-shadow: none;
  }

  ${reducedMotion};
`;

export const ErrorBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-bottom: 1.2rem;
  padding: 0.9rem 1.1rem;
  border-radius: ${radius.md};
  background: rgba(180, 84, 74, 0.09);
  border: 1px solid rgba(180, 84, 74, 0.28);
  color: ${color.danger};
  font-family: ${font.body};
  font-size: 0.88rem;
  animation: ${shake} 0.45s ${ease.out};
  ${reducedMotion};
`;

export const EmptyCart = styled.div`
  max-width: 30rem;
  margin: 3rem auto;
  padding: 3rem 2rem;
  text-align: center;
  border-radius: ${radius.lg};
  background: rgba(255, 255, 255, 0.9);
  box-shadow: ${shadow.soft};

  h3 {
    font-family: ${font.display};
    font-size: 1.9rem;
    color: ${color.ink};
    margin: 0 0 0.6rem;
  }

  p {
    font-family: ${font.body};
    color: ${color.inkSoft};
    margin: 0 0 1.6rem;
    line-height: 1.7;
  }
`;
